import {
  LUNCH_SECTIONS,
  customLunchSections,
  formatCents,
  lunchMealToCartId,
  mealsForDate,
  parseMoneyToCents,
  tt,
  type LunchMeal,
  type LunchWeek,
  type MoneyInput,
} from '@agos/shared'

import type { OrderCartLine } from './cart'

/**
 * The lunch tab on `/new-order` — the second piece the page had ported and was not calling.
 *
 * `mealsForDate`, `lunchMealToCartId`, `mondayOf` and the day arithmetic were all ported into
 * `@agos/shared/lunch` with tests, and nothing on this screen used them: a staff member taking a lunch
 * order by phone had to find the dish in the ordinary catalogue, where the daily menu does not appear.
 *
 * **The section labels are not the section keys, and in this data they are crossed.** Measured:
 * `settings.sectionLabels` is `{soup: "Polievky", pizza: "Burger", burger: "PIZZA",
 * "pasta-bolognese": "Cestoviny -"}`, and the meals agree with the labels rather than with the keys —
 * `mon-pizza-3` is *Sweet Bro*, described as *"Domáca žemľa, 130g 100% hovädzie mäso…"*, which is a
 * burger. So the label is the truth and the key is an identifier. Rendering the key, or
 * `LUNCH_SECTIONS`' built-in label, would put nine dishes under the wrong four headings.
 *
 * **And one type has no label at all.** Monday's nine meals include `type: "bowl"`, which is absent from
 * both `sectionLabels` and `LUNCH_SECTIONS`. It is not dropped and it does not print as `bowl` — it gets
 * a stated *unnamed section* heading, because a dish nobody can order is worse than an ugly heading, and
 * a raw key on a screen a customer is being read to from is worse still.
 *
 * **A soup has two prices.** `price` on its own, `bundlePrice` with a main — 1,50 € against 0,50 € in
 * this data. The tab shows both and says which is which; which one is charged depends on what else is in
 * the basket, and that is the caller's decision, not this module's.
 */

export interface LunchSectionView {
  type: string
  label: string
  /** True where the label had to be invented because the settings named none. */
  unnamed: boolean
  meals: LunchMeal[]
}

export interface LunchPayload {
  settings: Record<string, unknown>
  weeks: LunchWeek[]
}

export function lunchPayloadOf(payload: unknown): LunchPayload {
  const row = (payload ?? {}) as Record<string, unknown>
  return {
    settings: (row.settings ?? {}) as Record<string, unknown>,
    weeks: Array.isArray(row.weeks) ? (row.weeks as LunchWeek[]) : [],
  }
}

export function sectionLabelsOf(settings: Record<string, unknown>): Record<string, string> {
  const raw = settings.sectionLabels
  if (typeof raw !== 'object' || raw === null) return {}
  const labels: Record<string, string> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim() !== '') labels[key] = value.trim()
  }
  return labels
}

/** Whether the restaurant is showing a lunch menu at all. `visible` is the franchise's own switch. */
export function lunchIsOffered(payload: LunchPayload): boolean {
  return payload.settings.visible === true && payload.weeks.length > 0
}

/**
 * The day's meals, grouped into the sections the settings name.
 *
 * Order follows `LUNCH_SECTIONS` where a type is known — soups first, as they are on the printed menu —
 * and anything unrecognised trails it in the order the meals arrived, so nothing is lost to a sort.
 */
export function sectionsFor(payload: LunchPayload, date: string | null): LunchSectionView[] {
  const meals = mealsForDate(payload.weeks, date)
  if (meals.length === 0) return []
  const labels = sectionLabelsOf(payload.settings)
  // §9.315 - a section the franchise named itself carries its label here, not in `sectionLabels`.
  // Without this a *Lokša* the owner had just created printed as *Bez názvu sekcie* on the till: the
  // fallback below is for a type nobody named, and a deliberately named section is not that.
  const named = new Map(
    customLunchSections(payload.settings.customSections).map((section) => [
      section.type,
      section.label,
    ]),
  )

  const byType = new Map<string, LunchMeal[]>()
  for (const meal of meals) {
    const bucket = byType.get(meal.type)
    if (bucket === undefined) byType.set(meal.type, [meal])
    else bucket.push(meal)
  }

  const known = LUNCH_SECTIONS.map((section) => String(section.type))
  const ordered = [
    ...known.filter((type) => byType.has(type)),
    ...[...byType.keys()].filter((type) => !known.includes(type)),
  ]

  return ordered.map((type) => {
    // The settings' label wins over the built-in one: in this data they disagree, and the settings are
    // what the restaurant printed. A custom section's own label comes after that and before the
    // fallback, so renaming one through `sectionLabels` still wins.
    const label = labels[type] ?? named.get(type)
    return {
      type,
      label: label ?? tt('Bez názvu sekcie'),
      unnamed: label === undefined,
      meals: byType.get(type) ?? [],
    }
  })
}

/** What a meal costs on its own, and what it costs with a main where that differs. */
export function mealPriceNote(meal: LunchMeal): string {
  const alone = parseMoneyToCents(meal.price)
  const bundle = meal.bundlePrice === undefined ? null : parseMoneyToCents(meal.bundlePrice)
  if (bundle === null || bundle === alone) return formatCents(alone)
  return `${formatCents(alone)} · ${tt('s hlavným jedlom')} ${formatCents(bundle)}`
}

/** Said above an empty day, because a closed Sunday and a menu nobody wrote are different things. */
export function emptyDayNote(payload: LunchPayload, date: string | null): string {
  if (!lunchIsOffered(payload)) {
    return tt('Obedové menu je vypnuté v nastaveniach - na tento deň sa nedá objednať.')
  }
  if (date === null) return tt('Vyberte deň.')
  return tt('Na tento deň nie je zapísané žiadne obedové menu.')
}

/**
 * A lunch meal as a cart line.
 *
 * `lunchMealToCartId` hashes the meal's string id to a **negative** number, which is how a daily-menu
 * dish is told apart from a catalogue product whose ids are positive — the same convention the kitchen
 * board and the order payload already use.
 */
export function lunchCartLine(meal: LunchMeal): {
  productId: number
  name: string
  unitPriceCents: number
} {
  return {
    productId: lunchMealToCartId(meal),
    name: meal.name.trim(),
    unitPriceCents: parseMoneyToCents(meal.price),
  }
}

/**
 * Re-prices the cart's soups against the mains beside them — §9.118, the owner's point 4.
 *
 * **The manager billed every soup at its à-la-carte price.** `lunchCartLine` returns `meal.price` and
 * nothing downstream ever looked at `bundlePrice`, so a soup ordered with a main went out at 1,50 €
 * on the phone and 0,50 € on the customer's own screen, from the same menu on the same day.
 *
 * **Why a pass and not a decision at add time.** Legacy needs `promoteOneSoup` when a main is added
 * and `demoteOneSoup` when one is removed; the remove half is the one that gets forgotten, and then
 * a cancelled main leaves a soup at the bundle price forever. A pass over the whole cart has no
 * halves — run it after every mutation and the cart is always in canonical form. It also survives
 * `addProduct` merging the two same-id lines back together, because the next pass splits them again.
 *
 * **Both variants keep the meal's own negative id.** `ServerPricing.lunchPrices` keys on
 * `cartIdOf(meal.id)` and allows *both* prices for a soup, so a 0,50 € line under that id is accepted
 * as sent. Legacy's `id ± 50000` trick cannot be copied: a fabricated id is not in `lunchPrices` at
 * all, falls into the *composed line* branch, and is priced as something it is not.
 *
 * Lines the day's menu does not name are returned untouched — a catalogue product that happens to be
 * a soup is not this function's business.
 */
export function repriceLunchSoups<T extends OrderCartLine>(
  cart: readonly T[],
  meals: readonly LunchMeal[],
  settingsBundlePrice: MoneyInput | null | undefined,
): T[] {
  const byCartId = new Map<number, LunchMeal>()
  for (const meal of meals) byCartId.set(lunchMealToCartId(meal), meal)
  if (byCartId.size === 0) return [...cart]

  const mealOf = (line: OrderCartLine): LunchMeal | undefined => byCartId.get(line.productId)
  const isSoup = (line: OrderCartLine): boolean => mealOf(line)?.type === 'soup'

  // A main is any lunch line that is not a soup. Catalogue lines do not pair — the menu's own price
  // is what the bundle is a discount against, and a pizza off the à-la-carte list is not that.
  let remaining = cart.reduce(
    (total, line) => (mealOf(line) !== undefined && !isSoup(line) ? total + line.quantity : total),
    0,
  )

  const priced: T[] = []
  for (const line of cart) {
    const meal = mealOf(line)
    if (meal === undefined || meal.type !== 'soup') {
      priced.push(line)
      continue
    }

    const own = parseMoneyToCents(meal.price)
    const bundle = bundleCentsFor(meal, settingsBundlePrice)
    // No discount to give: at or above the meal's own price, "bundled" would print (s menu) beside a
    // price that did not change.
    if (bundle === null || bundle >= own) {
      priced.push({ ...line, unitPrice: own / 100 })
      continue
    }

    const bundled = Math.min(remaining, line.quantity)
    remaining -= bundled
    if (bundled > 0) {
      priced.push({ ...line, unitPrice: bundle / 100, quantity: bundled, lunchMealType: 'soup' })
    }
    if (bundled < line.quantity) {
      priced.push({
        ...line,
        unitPrice: own / 100,
        quantity: line.quantity - bundled,
        lunchMealType: 'soup',
      })
    }
  }
  return priced
}

/** What one soup costs alongside a main: the meal's own `bundlePrice`, else the franchise setting. */
function bundleCentsFor(meal: LunchMeal, settingsBundlePrice: MoneyInput | null | undefined): number | null {
  if (meal.bundlePrice !== undefined && meal.bundlePrice !== null) {
    return parseMoneyToCents(meal.bundlePrice)
  }
  if (settingsBundlePrice === null || settingsBundlePrice === undefined) return null
  return parseMoneyToCents(settingsBundlePrice)
}

/** The franchise's default soup-with-a-main price, as the lunch payload carries it. */
export function soupBundlePriceOf(payload: LunchPayload): MoneyInput | null {
  const raw = payload.settings.soupBundlePrice
  return typeof raw === 'number' || typeof raw === 'string' ? raw : null
}
